import PropTypes from 'prop-types'
import React from 'react'
import Input from 'components/Input'
import updateSearchAction from 'modules/search/actions/update-search'
import useSelector from 'modules/component/utils/hooks/useSelector.js'
import useDispatch from 'modules/component/utils/hooks/useDispatch.js'
import getQuerySearchSelect from 'modules/search/selectors/get-query'
import { logUtil } from 'packages/src'
import getSearchLocationUtil from 'modules/location/utils/get-search'
import getQueryParamsFromStringObjectUtil from 'utils/object/get-query-params-from-string'
import updateQueryParameterSearchAction from 'modules/search/actions/update-query-parameter'
import getUrlQuerySearchSelect from 'modules/search/selectors/get-url-query'
import formatMessageIntlUtil from 'modules/intl/utils/format-message/index.js'
import { defineMessages } from 'react-intl'
import useIntl from 'modules/component/utils/hooks/useIntl.js'

const AUTO_SUBMIT_TIMER_IN_MS = 300
const messages = defineMessages({
  search: {
    id: 'SearchInput.search',
    defaultMessage: 'Search'
  }
})

/**
 * @param placeholder
 * @param isFocused
 * @param onSubmit
 * @returns {JSX.Element}
 * @constructor
 */
export default function SearchInput ({
  placeholder,
  isFocused,
  onSubmit
}) {
  const dispatch = useDispatch()
  const query = useSelector(getQuerySearchSelect)
  const timerIdRef = React.useRef()
  const urlQuery = useSelector(getUrlQuerySearchSelect)
  const intl = useIntl()

  const debugProps = {
    query,
    urlQuery
  }
  logUtil('SearchInput').debug('debugProps=', debugProps)

  React.useEffect(() => {
    const search = getSearchLocationUtil()
    const initialQuery = getQueryParamsFromStringObjectUtil(search).q
    const debugProps = {
      search,
      initialQuery,
      query
    }
    logUtil('SearchInput#mount').debug('debugProps=', debugProps)
    if (initialQuery && initialQuery !== query) {
      dispatch(updateSearchAction(initialQuery))
    }
  }, [])

  const submit = (query) => {
    logUtil('SearchInput#submit').debug('query=', query)
    onSubmit(query)
    dispatch(updateQueryParameterSearchAction(query))
  }

  React.useEffect(() => {
    if (urlQuery && urlQuery !== query) {
      dispatch(updateSearchAction(urlQuery))
      submit(urlQuery)
    }
  }, [urlQuery])

  React.useEffect(() => {
    const shouldAutoSubmit = !!query && query !== urlQuery
    const debugProps = {
      shouldAutoSubmit,
      query,
      urlQuery
    }
    logUtil('SearchInput#queryChange').debug('debugProps=', debugProps)

    if (shouldAutoSubmit) {
      timerIdRef.current = setTimeout(() => {
        submit(query)
      }, AUTO_SUBMIT_TIMER_IN_MS)
    }

    return () => {
      clearAutoSubmitTimer()
    }
  }, [query])

  const clearAutoSubmitTimer = () => {
    if (!timerIdRef.current) {
      return
    }
    clearTimeout(timerIdRef.current)
    timerIdRef.current = null
  }

  const handleChange = (event, name, value) => {
    dispatch(updateSearchAction(value))
  }

  const handleSubmit = () => {
    clearAutoSubmitTimer()
    submit(query)
  }

  const placeholderToUse = (
    placeholder ||
    formatMessageIntlUtil(intl, messages.search)
  )
  return (
    <Input
      placeholder={placeholderToUse}
      value={query}
      isFocused={isFocused}
      isBig
      onChange={handleChange}
      onEnter={handleSubmit}
    />
  )
}

SearchInput.propTypes = {
  isFocused: PropTypes.bool,
  onSubmit: PropTypes.func
}
